import React, { useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { primaryColor } from "utils/colors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { fetchPublicPromotions } from "api/auth";
import "./PromotionBanner.scss";
import { PublicPromotion } from "interfaces/IPromotion";
import stars from "assets/estrellitas.png";

const PromotionBanner: React.FC = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [promotions, setPromotions] = useState<PublicPromotion[]>([]);
  const [animationDirection, setAnimationDirection] = useState<
    "left" | "right" | null
  >(null);
  const [backTransition, setBackTransition] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  /**
   * Effect hook to load promotions when component mounts
   * Fetches public promotions from API and updates state
   */
  useEffect(() => {
    const loadPromotions = async () => {
      try {
        const response = await fetchPublicPromotions();
        setPromotions(response);
      } catch (error) {
        console.error("Error loading promotions:", error);
      }
    };
    loadPromotions();
  }, []);

  /**
   * Handles transition to previous promotion
   * Updates animation direction and selected index with proper transitions
   */
  const handlePrevious = () => {
    const nextIndex = (selectedIndex + 1) % promotions.length;
    setBackTransition(promotions[nextIndex].id);
    setAnimationDirection("left");
    requestAnimationFrame(() => {
      setSelectedIndex(
        (prevIndex) => (prevIndex - 1 + promotions.length) % promotions.length
      );
    });
  };

  /**
   * Handles transition to next promotion
   * Updates animation direction and selected index with proper transitions
   */
  const handleNext = () => {
    const prevIndex =
      (selectedIndex - 1 + promotions.length) % promotions.length;
    setBackTransition(promotions[prevIndex].id);
    setAnimationDirection("right");
    requestAnimationFrame(() => {
      setSelectedIndex((prevIndex) => (prevIndex + 1) % promotions.length);
    });
  };

  /**
   * Interface extending PublicPromotion to include position and transition states
   */
  interface PromotionWithPosition extends PublicPromotion {
    position: "left" | "center" | "right" | "hidden";
    isBack?: boolean;
  }

  /**
   * Returns array of visible promotions with their positions
   * Handles different cases for 1, 2 or 3+ promotions
   * @returns Array of promotions with position information
   */
  const getVisiblePromotions = (): PromotionWithPosition[] => {
    if (!promotions.length) {
      return [];
    }
    if (promotions.length === 1) {
      return [{ ...promotions[0], position: "center" }];
    }

    if (promotions.length === 2) {
      return [
        {
          ...promotions[selectedIndex],
          position: "center",
        },
        {
          ...promotions[(selectedIndex + 1) % 2],
          position: "right",
          isBack: true,
        },
      ];
    }

    const prevIndex =
      (selectedIndex - 1 + promotions.length) % promotions.length;
    const nextIndex = (selectedIndex + 1) % promotions.length;

    return [
      {
        ...promotions[prevIndex],
        position: "left",
        isBack: backTransition === promotions[prevIndex].id,
      },
      {
        ...promotions[selectedIndex],
        position: "center",
      },
      {
        ...promotions[nextIndex],
        position: "right",
        isBack: backTransition === promotions[nextIndex].id,
      },
    ];
  };

  /**
   * Handles click events on promotion images
   * Triggers previous/next transitions based on clicked position
   * @param promotion The clicked promotion with position information
   */
  const handleImageClick = (promotion: PromotionWithPosition) => {
    if (promotion.position === "left") {
      handlePrevious();
    } else if (promotion.position === "right") {
      handleNext();
    }
  };
  /**
   * Calculates translation value based on window width
   * Returns appropriate rem value for responsive design
   * @returns number Translation value in rem units
   */
  const getTranslateValue = () => {
    if (window.innerWidth <= 768) {
      return 10;
    } else if (window.innerWidth > 768 && window.innerWidth <= 968) {
      return 15;
    } else if (window.innerWidth > 968 && window.innerWidth <= 1100) {
      return 17;
    } else if (window.innerWidth > 1100 && window.innerWidth <= 1200) {
      return 20;
    } else if (window.innerWidth > 1200 && window.innerWidth <= 1300) {
      return 23;
    } else if (window.innerWidth > 1300 && window.innerWidth <= 1400) {
      return 26;
    } else if (window.innerWidth > 1400 && window.innerWidth <= 1500) {
      return 29;
    } else if (window.innerWidth > 1500 && window.innerWidth <= 1600) {
      return 32;
    }
    return 34; // Original 34rem for desktop
  };

  /**
   * Effect hook to handle window resize events
   * Updates mobile/tablet states and translation values
   */
  const [translateValue, setTranslateValue] = useState(getTranslateValue());
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setTranslateValue(getTranslateValue());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (promotions.length > (isMobile ? 1 : 2)) {
      const autoSlideTimer = setInterval(() => {
        handleNext();
      }, 8000); 

      return () => clearInterval(autoSlideTimer);
    }
  }, [promotions.length, isMobile]);

  return (
    <Container fluid className="p-0" id="promotion-banner">
      {promotions.length > 0 && (
        <>
          <div
            style={{
              backgroundColor: primaryColor,
              padding: "20px 0",
              opacity: 0.9,
            }}
          >
            <h1
              style={{
                color: "white",
                textAlign: "center",
                fontFamily: "Proelium",
                fontWeight: "bold",
                fontSize: translateValue < 11 ? "40px" : "64px",
                letterSpacing: "0.38em",
                display: "flex",
                justifyContent: "center",
                margin: 0,
              }}
            >
              ¡OFERTAS!
            </h1>
          </div>

          <div className="position-relative">
            <div className="thumbnails-container">
              {getVisiblePromotions().map((promotion, index) => {
                const isCenter = promotion.position === "center";
                const isSmallSet = promotions.length <= 2;

                return (
                  <div
                    key={`${index}-${promotion.id}`}
                    className={`thumbnail-wrapper ${
                      !isSmallSet
                        ? `position-${
                            promotion.position
                          } sliding-${animationDirection} ${
                            backTransition === promotion.id ? "back" : ""
                          }`
                        : ""
                    }`}
                    style={{
                      transform:
                        isMobile || isSmallSet
                          ? `translateX(0) scale(${isCenter ? 1 : 0})`
                          : `translateX(${
                              (index - 1) * translateValue
                            }rem) scale(${isCenter ? 1 : 0.8})`,

                      opacity: isCenter ? 1 : isMobile || isSmallSet ? 0 : 0.8,
                      transition:
                        isMobile || isSmallSet
                          ? "none"
                          : "transform 0.8s ease-in-out, opacity 0.8s ease-in-out",
                      zIndex: isCenter ? 2 : 1,
                    }}
                    onClick={() => handleImageClick(promotion)}
                  >
                    <img
                      src={promotion.image}
                      alt={promotion.name}
                      className="thumbnail-image"
                    />
                  </div>
                );
              })}
            </div>

            <div
              className="promotion-description"
              style={{ margin: promotions?.length === 0 ? 0 : "30px 0" }}
            >
              <h2 style={{ fontSize: translateValue < 11 ? "20px" : "36px" }}>
                {promotions[selectedIndex]?.description}
              </h2>
            </div>
            {promotions.length > 1 && (
              <>
                <button
                  className="carousel-control prev"
                  onClick={handlePrevious}
                  style={{
                    left:
                      translateValue < 34
                        ? translateValue < 29
                          ? "2%"
                          : "0%"
                        : "3%",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faChevronLeft}
                    size={translateValue < 29 ? "2x" : "3x"}
                  />
                </button>
                <button
                  className="carousel-control next"
                  onClick={handleNext}
                  style={{
                    right:
                      translateValue < 34
                        ? translateValue < 29
                          ? "2%"
                          : "0%"
                        : "3%",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    size={translateValue < 29 ? "2x" : "3x"}
                  />
                </button>
              </>
            )}
          </div>
        </>
      )}
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ margin: promotions.length === 0 ? "10px 0 0 0" : "40px 0" }}
      >
        {translateValue >= 11 && (
          <div
            style={{
              height: "1px",
              background: primaryColor,
              flex: 1,
              maxWidth: "32%",
              boxShadow: "2px 4px 3px rgba(0,0,0,0.6)",
            }}
          />
        )}
        <h1
          style={{
            color: "black",
            fontWeight: "bold",
            fontSize: translateValue < 11 ? "40px" : "64px",
            letterSpacing: "3%",
            display: "flex",
            alignItems: "center",
            marginLeft: translateValue < 11 ? "3rem" : "4rem",
          }}
        >
          NOVEDADES
       
        </h1>
        <img
            src={stars}
            alt="estrella"
            style={{
              width: translateValue < 11 ? "3rem" : "4rem",
              height: translateValue < 11 ? "3rem" : "4rem",
              verticalAlign: "middle",
            }}
          />
        {translateValue >= 11 && (
          <div
            style={{
              height: "1px",
              background: primaryColor,
              flex: 1,
              maxWidth: "32%",
              boxShadow: "2px 4px 3px rgba(0,0,0,0.6)",
            }}
          />
        )}
      </div>
    </Container>
  );
};

export default PromotionBanner;
